import React, {useCallback, useRef, useState} from "react";
import cn from "classnames";
import classes from "./UserOption.module.css";
import UserItem from "./UserItem";
import Checkbox from "../../ui/checkbox";
import {PickedUser} from "./UsersSelect";
import AssignModalFailReasonText from "./AssignFailReasons";
import Dropdown, {DropdownContent, DropdownRef, DropdownReference} from "../../ui/dropdown";
import IconButton from "../../ui/iconbutton";
import {CaretDown} from "../../ui/icons";
import {useTranslation} from "react-i18next";
import {formatToMoscowDate} from "../../time";
import {AssignmentFailReason, ChallengeParticipationState} from "../../schema";
import Table from "../../ui/table";

type UserOptionProps = {
  user?: PickedUser;
  email?: string;
  showCheckbox?: boolean;
  forNewUsers?: boolean;
  checked?: boolean;
  disabled?: boolean | undefined;
  onSelect?: React.ChangeEventHandler<HTMLInputElement>;
  forShow?: boolean;
};

export const UserOption: React.FC<UserOptionProps> = ({
  user,
  email,
  showCheckbox = false,
  forNewUsers = false,
  checked = false,
  disabled = false,
  forShow,
  onSelect,
}) => {
  const hasErrors = user && user.failReasons && user.failReasons?.length > 0;
  const ctxMenuRef = useRef<DropdownRef>(null);
  const [tableVisibility, setTableVisibility] = useState<boolean>();

  const toggleTableVisibility = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!ctxMenuRef.current) {
      return;
    }
    if (!ctxMenuRef.current.state) {
      ctxMenuRef.current.open();
      setTableVisibility(true);
    } else {
      ctxMenuRef.current.close();
      setTableVisibility(false);
    }
  }, []);
  const isError =
    user &&
    user?.failReasons &&
    (user.failReasons.includes(AssignmentFailReason.ALREADY_ASSIGNED) ||
      user.failReasons.includes(AssignmentFailReason.INACTIVE_USER));

  const isWarn = user && user?.failReasons && user.failReasons.includes(AssignmentFailReason.PARTLY_ASSIGNED);

  return (
    <label
      key={user ? user?.email : email}
      className={cn(
        classes.usersListItem,
        {[classes.withCheckbox]: showCheckbox},
        forNewUsers ? classes.new : email ? classes.error : null,
        !forShow && user && isError ? classes.error : null,
        !forShow && user && isWarn && !isError ? classes.warn : null,
      )}
      data-testid="user-option"
    >
      {email ? email : null}
      {user && <UserItem user={user} />}
      {showCheckbox && (
        <div className={classes.checkboxBlock}>
          {hasErrors && (
            <div className={classes.errorsBlock}>
              {user.failReasons?.map((reason, index) => (
                <div className={classes.flex} key={index}>
                  <span
                    className={cn(
                      classes.reason,
                      user && isError ? classes.error : null,
                      user && isWarn && reason === AssignmentFailReason.PARTLY_ASSIGNED ? classes.warn : null,
                    )}
                    key={reason}
                  >
                    <AssignModalFailReasonText reason={reason} />
                  </span>
                  {(reason === AssignmentFailReason.ALREADY_ASSIGNED ||
                    reason === AssignmentFailReason.PARTLY_ASSIGNED) && (
                    <Dropdown ref={ctxMenuRef} stopCloseOnOuterClick>
                      <DropdownReference onClick={toggleTableVisibility}>
                        <IconButton
                          className={cn(classes.toggleButton, {[classes.checked]: tableVisibility})}
                          size="s"
                          icon={CaretDown}
                        />
                      </DropdownReference>
                      <DropdownContent className={classes.tableWrapper} offset={[0, 12]}>
                        {user.challenges && <AlreadyAssignedChallengesTable challenges={user.challenges} />}
                      </DropdownContent>
                    </Dropdown>
                  )}
                </div>
              ))}
            </div>
          )}
          {(forNewUsers || (user && !isError)) && (
            <Checkbox
              checked={checked}
              disabled={disabled}
              onChange={onSelect}
              type="checkbox"
              data-id={email ? email : user?.id}
            />
          )}
        </div>
      )}
    </label>
  );
};

export function AlreadyAssignedChallengesTable({challenges}: {challenges: ChallengeParticipationState[]}) {
  const {t, i18n} = useTranslation();

  return (
    <Table className={cn(classes.alreadyAssignedChallengesTable)}>
      <Table.Head>
        <Table.Row>
          <Table.Cell>{t("components.UserOption.AlreadyAssignedChallengesTable.name")}</Table.Cell>
          <Table.Cell>{t("components.UserOption.AlreadyAssignedChallengesTable.from")}</Table.Cell>
          <Table.Cell>{t("components.UserOption.AlreadyAssignedChallengesTable.to")}</Table.Cell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {challenges.map((challenge, index) => (
          <Table.Row key={index}>
            <Table.Cell>
              <b>{challenge.name}</b>
              {challenge.projectName ? "/ " +
              t("components.MembersTable.project") + challenge.projectName : challenge.projectName}
            </Table.Cell>
            <Table.Cell>
              {challenge.activateAt ? " " + formatToMoscowDate(challenge.activateAt, i18n.language) : ""}
            </Table.Cell>
            <Table.Cell>
              {challenge.deactivateAt ? " " + formatToMoscowDate(challenge.deactivateAt, i18n.language) : ""}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
